import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Grid, useMediaQuery, useTheme } from "@mui/material";
import PipelineUpdates from "./PipelineUpdates";
import EventCalendar from "./EventCalendar";
import { RootState, useDispatch, useSelector } from "../../store";
import UserContext, {
  convertRoleToFriendlyName,
  doesUserHaveRole,
  getMaxUserRole,
  isUserInRoles,
  ServerOrgRoles,
  UserRoleGroups,
  userRoleInOrg,
} from "../../services/UserContext";
import { RequestStatus } from "../../utils/Helpers/fetchStatus";
import { useHistory } from "react-router-dom";
import {
  getDashboardData,
  getEvents,
  getOverviewData,
  getPipelineUpdatesDetails,
  resetFeedbackUpdateState,
  getDashboardStatistics,
  TimeRangeType,
} from "../../slices/dashboard";
import { getSearchParamValue } from "../../utils/Helpers/extractDataFromSearchParams";
import Resources from "./Resources";
import {
  getPipelines,
  resetPipelinesState,
  updatePipelines,
} from "../../slices/pipelines";
import toast from "react-hot-toast";
import { isEqual } from "lodash";
import { Userpilot } from "userpilot";
import SummaryCards from "./SummaryCards";

export type DashboardUpdatesTabType = "overview" | "details" | null;

const Dashboard: FC = (props) => {
  const {} = props;

  const dispatch = useDispatch();
  const context = useContext(UserContext);
  const history = useHistory();

  const {
    events,
    updates,
    fetchStatus,
    pipelineUpdates,
    opportunitiesDetails,
    user,
    organization,
    statistics,
  } = useSelector((state: RootState) => state.dashboard);
  const {
    fetchStatus: pipelinesFetchStatus,
    rawItems,
    items: pipelines,
  } = useSelector((state: RootState) => state.pipelines);

  const theme = useTheme();

  const isTallerThan660 = useMediaQuery("(min-height: 660px)");

  const summaryCardsContainerRef = useRef<HTMLDivElement>(null);
  const liveBidsContainerRef = useRef<HTMLDivElement>(null);
  const resourcesContainerRef = useRef<HTMLDivElement>(null);

  const isPlusUser = isUserInRoles(context, UserRoleGroups.plusOrHigher);
  const isMember = doesUserHaveRole(context, ServerOrgRoles.organizationMember);

  /**
   * used to keep track of all components' heights apart from events card, so that events card can always take up all possible space
   */
  const [combinedHeight, setCombinedHeight] = useState<number>(0);
  const [summaryCardsHeight, setSummaryCardsHeight] = useState<number>(0);

  const isLessThanMd = useMediaQuery(theme.breakpoints.down("md"));

  const userPilotRole = useMemo(() => {
    let isOrgAdmin = userRoleInOrg(context, ServerOrgRoles.organizationAdmin);
    let maxUserRole = getMaxUserRole(context);
    let role = convertRoleToFriendlyName(maxUserRole);
    if (
      maxUserRole === ServerOrgRoles.plus ||
      maxUserRole === ServerOrgRoles.pro
    )
      role = isOrgAdmin ? `${role} Admin` : role;

    return role;
  }, [context]);

  const pageIndex = useMemo(
    () => +getSearchParamValue<string>(history, "pageIndex", "0"),
    [history.location],
  );
  const pageSize = useMemo(
    () => +getSearchParamValue<string>(history, "pageSize", "20"),
    [history.location],
  );
  const opportunitiesKeyword = useMemo(
    () => getSearchParamValue<string>(history, "opportunitiesKeyword", ""),
    [history.location],
  );
  const eventsKeyword = useMemo(
    () => getSearchParamValue<string>(history, "eventsKeyword", ""),
    [history.location],
  );

  const overviewRange = useMemo(
    () =>
      getSearchParamValue<"today" | "last7" | "lastMonth" | "lastYear">(
        history,
        "updatesRange",
        "today",
      ),
    [history.location],
  );
  const overviewStartDate = useMemo(
    () =>
      overviewRange === "lastYear"
        ? new Date().addYears(-3)
        : overviewRange === "lastMonth"
          ? new Date().addMonths(-1)
          : new Date().addDays(overviewRange === "last7" ? -7 : -1),
    [overviewRange],
  );

  const datasetTypes = useMemo(
    () =>
      // stringify to prevent triggering useEffect even when the value doesn't change
      JSON.stringify(
        getSearchParamValue<("Forecast" | "Contract" | "Grant" | "All")[]>(
          history,
          "opportunitiesType",
          ["All"],
          {
            multiple: true,
          },
        ),
      ),
    [history.location],
  );

  const detailsForPipelines = useMemo(
    () =>
      getSearchParamValue<"true" | "false">(
        history,
        "detailsForPipelines",
        "false",
      ),
    [history.location],
  );
  const pipelineValue = useMemo(
    () =>
      // stringify to prevent triggering useEffect even when the value doesn't change
      JSON.stringify(
        getSearchParamValue<string[]>(history, "pipelineValue", ["all"], {
          multiple: true,
        }),
      ),
    [history.location],
  );

  const tab = useMemo(
    () =>
      getSearchParamValue<DashboardUpdatesTabType>(history, "tab", "overview"),
    [history.location],
  );

  const handleGetOverviewData = useCallback((): void => {
    if (!isPlusUser) return;
    dispatch(
      getOverviewData({
        context,
        values: {
          // startDate: overviewStartDate.toISOString(),
          startDate: new Date().addDays(-1).toISOString(),
          endDate: new Date().toISOString(),
        },
      }),
    );
  }, [
    // overviewStartDate,
    isPlusUser,
  ]);

  const handleGetPipelineUpdatesDetails = useCallback((): void => {
    if (!isPlusUser) return;
    // if (opportunitiesDetails.data.length === 0 && pageIndex > 0) {
    //   setUrlParams(history, { pageIndex: "0" }, { replace: true });
    // } else if (opportunitiesDetails.data.length === 0 && pageSize > 20) {
    //   setUrlParams(history, { pageSize: "20" }, { replace: true });
    // } else {
    //   const loadMore = pageIndex > 0;
    dispatch(
      getPipelineUpdatesDetails({
        context,
        params: {
          DatasetTypes: isEqual(JSON.parse(datasetTypes), ["All"])
            ? ["Forecast", "Contract", "Grant"]
            : JSON.parse(datasetTypes),
          PipelineIds: isEqual(JSON.parse(pipelineValue), ["all"])
            ? undefined
            : JSON.parse(pipelineValue),
          UpdatedAfter: overviewStartDate.toISOString(),
          UpdatedBefore: new Date().toISOString(),
          PageIndex: pageIndex,
          PageSize: pageSize,
          Keyword: opportunitiesKeyword,
          SortField: "lastUpdatedOn",
          SortOrder: -1,
          FilterToUserPipelines: detailsForPipelines,
        },
        // loadMore,
      }),
    );
    // }
  }, [
    pageSize,
    pageIndex,
    opportunitiesKeyword,
    overviewStartDate,
    datasetTypes,
    isPlusUser,
    detailsForPipelines,
    pipelineValue,
    opportunitiesDetails.data,
  ]);

  const getCombinedHeight = (): void => {
    if (!liveBidsContainerRef.current || !resourcesContainerRef.current) return;
    setCombinedHeight(
      liveBidsContainerRef.current.clientHeight +
        resourcesContainerRef.current.clientHeight,
    );
  };

  const getSummaryCardsHeight = (): void => {
    if (!summaryCardsContainerRef.current) return;
    setSummaryCardsHeight(summaryCardsContainerRef.current.clientHeight);
  };

  useEffect(() => {
    const handleResize = () => {
      getCombinedHeight();
      getSummaryCardsHeight();
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (!resourcesContainerRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      getCombinedHeight();
    });
    resizeObserver.observe(resourcesContainerRef.current);
    return () => resizeObserver.disconnect(); // clean up
  }, []);

  useEffect(() => {
    if (!summaryCardsContainerRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      getSummaryCardsHeight();
    });
    resizeObserver.observe(summaryCardsContainerRef.current);
    return () => resizeObserver.disconnect(); // clean up
  }, []);

  useEffect(() => {
    getCombinedHeight();
    getSummaryCardsHeight();
  });

  useEffect(() => {
    dispatch(getDashboardData(context));
    // dispatch(
    //   getEvents({ context, params: { EventNameKeyword: eventsKeyword } }),
    // );
    dispatch(getPipelines(context));
    // Fetch statistics on mount
    dispatch(
      getDashboardStatistics({
        context,
        timeRange: statistics.timeRange,
      }),
    );
  }, []);

  useEffect(() => {
    //it differs from regular fetch status value assignment since this requires updating the pipelines with additional data
    if (RequestStatus.isHalfDone(pipelinesFetchStatus)) {
      dispatch(updatePipelines({ pipelines: rawItems }));
    } else if (RequestStatus.isError(pipelinesFetchStatus)) {
      toast.error("Couldn't load pipelines details. Try again later.");
      dispatch(resetPipelinesState());
    }
  }, [pipelinesFetchStatus]);

  useEffect(() => {
    handleGetOverviewData();
    handleGetPipelineUpdatesDetails();
  }, [
    overviewRange,
    tab,
    opportunitiesKeyword,
    datasetTypes,
    pageIndex,
    pageSize,
    detailsForPipelines,
    pipelineValue,
  ]);

  useEffect(() => {
    if (
      RequestStatus.isDone(updates.feedback.fetchStatus) ||
      RequestStatus.isDone(updates.updateFeedback.fetchStatus) ||
      RequestStatus.isDone(updates.deleteFeedback.fetchStatus)
    ) {
      handleGetOverviewData();
      dispatch(resetFeedbackUpdateState());
    }
  }, [
    updates.feedback.fetchStatus,
    updates.updateFeedback.fetchStatus,
    updates.deleteFeedback.fetchStatus,
  ]);

  useEffect(() => {
    dispatch(
      getEvents({
        context,
        params: {
          Keyword: eventsKeyword,
        },
      }),
    );
  }, [eventsKeyword]);

  useEffect(() => {
    if (RequestStatus.isDone(fetchStatus)) {
      const today = new Date();
      const created_at =
        today.getFullYear() +
        "-" +
        (today.getMonth() + 1) +
        "-" +
        today.getDate();
      const id = context.user.parsedIdToken?.sub;
      const email = context.user.parsedIdToken?.email ?? "";

      Userpilot.identify(id ?? "", {
        name: user?.firstName ?? "",
        email: email,
        role: userPilotRole,
        created_at: created_at,
        organization_name: organization?.name ?? "",
        tier: organization?.tier ?? "",
        expiration: organization?.tierExpiration ?? "",
      });
    }
  }, [fetchStatus]);

  const handleTimeRangeChange = (timeRange: TimeRangeType): void => {
    dispatch(
      getDashboardStatistics({
        context,
        timeRange,
      }),
    );
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2, flex: 1 }}>
        <Grid item xs={12} ref={summaryCardsContainerRef}>
          <SummaryCards
            statistics={statistics.data}
            timeRange={statistics.timeRange}
            onTimeRangeChange={handleTimeRangeChange}
            loading={RequestStatus.isFetching(statistics.fetchStatus)}
          />
        </Grid>
        <Grid
          container
          item
          sm={12}
          md={6}
          lg={8}
          spacing={2}
          sx={{
            ...(isPlusUser
              ? { flex: 1 }
              : {
                  height: "fit-content",
                }),
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              flex: 1,
              ...(isLessThanMd
                ? {
                    minHeight: "75vh",
                  }
                : {
                    maxHeight: `calc(100vh - 88px - ${summaryCardsHeight}px)`,
                  }),
            }}
          >
            <PipelineUpdates
              updates={updates}
              pipelineUpdates={pipelineUpdates}
              opportunitiesDetailsFetchStatus={opportunitiesDetails.fetchStatus}
              opportunitiesTotal={opportunitiesDetails.total}
            />
          </Grid>
        </Grid>
        <Grid
          container
          item
          sm={12}
          md={6}
          lg={4}
          spacing={2}
          sx={{
            height: "fit-content",
            ...(!isTallerThan660 && { minHeight: "580px" }),
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              flex: 1,
              ...(isLessThanMd
                ? {}
                : isTallerThan660
                  ? {
                      maxHeight: `calc(100vh - 88px - ${summaryCardsHeight}px - ${combinedHeight}px)`,
                    }
                  : { height: "235px" }),
            }}
          >
            <EventCalendar
              events={events.items}
              total={events.total}
              pageIndex={events.pageIndex}
              pageSize={events.pageSize}
              loading={RequestStatus.isFetching(events.fetchStatus)}
              loadDataAsync={() =>
                dispatch(
                  getEvents({ context, params: { name: eventsKeyword } }),
                )
              }
            />
          </Grid>
          <Grid
            item
            ref={liveBidsContainerRef}
            xs={12}
            sx={{
              height: "fit-content",
              p: `0 !important`,
            }}
          >
            {/*<AnticipatedLiveBids*/}
            {/*  loading={RequestStatus.isFetching(pipelinesFetchStatus)}*/}
            {/*  pipelines={pipelines}*/}
            {/*/>*/}
          </Grid>
          <Grid
            item
            ref={resourcesContainerRef}
            xs={12}
            sx={{
              height: "fit-content",
            }}
          >
            <Resources />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default Dashboard;
